import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCategories, createCategory, createItem as apiCreateItem, getItem, updateItem as apiUpdateItem, scrapeItemData, getMe } from '../api/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import AuthModal from '../components/common/AuthModal';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import { Loader2, UploadCloud, Info, X, Type, FileText, Tag, DollarSign, Image as ImageIcon, Plus, Briefcase, Package, Globe, Instagram, Facebook, Map, ChevronLeft, ChevronRight, Save, Navigation, Link as LinkIcon, ChevronDown, Search } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ImageWithFallback from '../components/common/ImageWithFallback';
import { CATEGORY_ATTRIBUTES, getCategoryAttrKey } from '../utils/categoryAttributes';
import SEO from '../components/common/SEO';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CategoryIcon = ({ name, className = "w-4 h-4" }) => {
  const iconName = name ? name.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('') : 'Tag';
  const IconCmp = LucideIcons[iconName] || LucideIcons[name] || Tag;
  return <IconCmp className={className} />;
};

const SortableImage = ({ img, i, total, removeImage, setMainImage, moveImage }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: img.preview });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={`relative aspect-square rounded-2xl overflow-hidden border-2 transition-all group ${img.isMain ? 'border-primary shadow-lg shadow-primary/20' : 'border-white/5'} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}>
        <ImageWithFallback src={img.preview} alt="" className="w-full h-full object-cover pointer-events-none" />
        
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 px-2" onPointerDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          {!img.isMain && (
            <button 
              type="button"
              onPointerDown={(e) => { e.stopPropagation(); setMainImage(i); }}
              onTouchStart={(e) => { e.stopPropagation(); setMainImage(i); }}
              className="w-full py-1.5 bg-primary text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg"
            >
              Make Main
            </button>
          )}
          
          <div className="flex w-full justify-between gap-1">
             <button 
                type="button" 
                onPointerDown={(e) => { e.stopPropagation(); moveImage(i, i - 1); }}
                onTouchStart={(e) => { e.stopPropagation(); moveImage(i, i - 1); }}
                disabled={i === 0} 
                className="flex-1 py-1.5 flex justify-center bg-white/20 hover:bg-white/40 text-white rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
             >
                <ChevronLeft size={14} />
             </button>
             <button 
                type="button" 
                onPointerDown={(e) => { e.stopPropagation(); moveImage(i, i + 1); }}
                onTouchStart={(e) => { e.stopPropagation(); moveImage(i, i + 1); }}
                disabled={i === total - 1} 
                className="flex-1 py-1.5 flex justify-center bg-white/20 hover:bg-white/40 text-white rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
             >
                <ChevronRight size={14} />
             </button>
          </div>

          <button 
            type="button"
            onPointerDown={(e) => { e.stopPropagation(); removeImage(i); }}
            onTouchStart={(e) => { e.stopPropagation(); removeImage(i); }}
            className="w-full py-1.5 bg-red-500 text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-red-600 transition-colors"
          >
            Remove
          </button>
        </div>

        {img.isMain && (
          <div className="absolute top-2 left-2 px-3 py-1 bg-primary text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-xl border border-white/20 z-10 pointer-events-none">
            ★ Main Photo
          </div>
        )}
    </div>
  );
};

export default function CreateItem({ id: propsId, onSuccess }) {
  const { id: paramId } = useParams();
  const id = propsId || paramId;
  const isEdit = !!id;
  const isModal = !!propsId;
  const { register, handleSubmit, control, setValue, reset, watch, trigger, formState: { errors, isDirty } } = useForm();
  const [showBrandInput, setShowBrandInput] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { data: user } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: getMe,
    retry: false,
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const { currency } = useCurrency();
  const queryClient = useQueryClient();
  const [images, setImages] = useState([]); // Array of { file, preview, isMain, isExisting }
  const [originalImages, setOriginalImages] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('Tag');
  const [hasImageChanges, setHasImageChanges] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [scrapedImportUrl, setScrapedImportUrl] = useState(null);
  const [acceptedImportTerms, setAcceptedImportTerms] = useState(false);
  const [isScraping, setIsScraping] = useState(false);

  const [showIconDropdown, setShowIconDropdown] = useState(false);
  const iconDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
        if (iconDropdownRef.current && !iconDropdownRef.current.contains(e.target)) {
            setShowIconDropdown(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const draft = localStorage.getItem('pendingItemDraft');
    if (draft && user) {
      try {
        const parsed = JSON.parse(draft);
        reset(parsed);
        toast.info(t('draftRestored', 'We restored your typed item details. Please re-select your photos and submit again.'));
        localStorage.removeItem('pendingItemDraft');
      } catch (e) {}
    }
  }, [user, reset, t]);

  const handleLoginSuccess = () => {
    if (pendingSubmitData) {
      submitWithUser(pendingSubmitData);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setImages((items) => {
        const oldIndex = items.findIndex(item => item.preview === active.id);
        const newIndex = items.findIndex(item => item.preview === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((img, i) => ({ ...img, isMain: i === 0 }));
      });
    }
  };

  const moveImage = (fromIndex, toIndex) => {
    if (fromIndex < 0 || toIndex < 0 || toIndex >= images.length) return;
    setImages((items) => {
      const newItems = arrayMove(items, fromIndex, toIndex);
      return newItems.map((img, i) => ({ ...img, isMain: i === 0 }));
    });
  };

  const handleImport = async () => {
    if (!importUrl) {
      toast.error(t('pleaseProvideUrl', 'Please provide a Facebook Marketplace URL'));
      return;
    }

    if (!importUrl.startsWith('http://') && !importUrl.startsWith('https://')) {
        toast.error(t('invalidImportUrl', 'Please provide a valid URL starting with http:// or https://'));
        return;
    }

    try {
      setIsScraping(true);
      const data = await scrapeItemData(importUrl);
      console.log('Import Result:', data);
      
      const currentValues = watch();
      let detectedCategoryId = currentValues.category;

      if (data.category && categories.length > 0) {
          const category = categories.find(c => c.name === data.category || c.label_en?.toLowerCase() === data.category);
          if (category) detectedCategoryId = category._id;
      }

      // Reset the form with merged data - this is the most reliable way to update the UI
      reset({
          ...currentValues,
          title: data.title || currentValues.title,
          description: data.description || currentValues.description,
          estimated_value: data.estimated_value || currentValues.estimated_value,
          category: detectedCategoryId,
          ...(data.attributes || {})
      });

      if (data.attributes?.brand) {
          setShowBrandInput(true);
      }
      
      if (data.images && data.images.length > 0) {
          const newImages = data.images.map((img, index) => ({
              file: img,
              preview: img,
              isMain: images.length === 0 && index === 0,
              isExisting: true 
          }));
          setImages(prev => [...prev, ...newImages]);
          toast.success(t('importSuccessWithImages', 'Imported data and images!'));
      } else {
          toast.success(t('importSuccess', 'Data imported successfully!'));
      }
      setScrapedImportUrl(importUrl);
      setImportUrl('');
      
      // Trigger validation to show red signs for missing required fields
      setTimeout(() => trigger(), 50);

    } catch (error) {
      toast.error(error.message || t('importFailed', 'Failed to import from this URL'));
    } finally {
      setIsScraping(false);
    }
  };

  // Watch form values for change detection
  const formValues = watch();

  // Fetch item data for editing
  const { data: itemToEdit, isLoading: isLoadingItem } = useQuery({
    queryKey: ['item', id],
    queryFn: () => getItem(id),
    enabled: isEdit,
  });

  useEffect(() => {
    if (isEdit && itemToEdit) {
        setShowBrandInput(!!itemToEdit.brand);
    }
  }, [isEdit, itemToEdit]);

  // Correctly populate form data when item is loaded
  useEffect(() => {
    if (isEdit && itemToEdit) {
        reset({
            title: itemToEdit.title,
            description: itemToEdit.description,
            category: itemToEdit.category?._id || itemToEdit.category,
            estimated_value: itemToEdit.estimated_value,
            condition: itemToEdit.condition,
            location: itemToEdit.location,
            looking_for: itemToEdit.looking_for || [],
            cash_flexibility: itemToEdit.cash_flexibility,
            open_to_other_offers: itemToEdit.open_to_other_offers,
            brand: itemToEdit.brand || '',
            is_visible: itemToEdit.is_visible !== undefined ? itemToEdit.is_visible : true,
            is_available: itemToEdit.is_available !== undefined ? itemToEdit.is_available : true,
            website: itemToEdit.website,
            social_instagram: itemToEdit.social_instagram,
            social_facebook: itemToEdit.social_facebook,
            google_reviews_link: itemToEdit.google_reviews_link,
            ...(itemToEdit.attributes || {})
        });
        const existingImages = (itemToEdit.images || []).map((img, i) => ({
            file: img,
            preview: img,
            isMain: i === 0,
            isExisting: true
        }));
        setImages(existingImages);
        setOriginalImages(JSON.stringify(existingImages.map(img => img.preview)));
        setHasImageChanges(false);
    }
  }, [isEdit, itemToEdit, reset]);

  useEffect(() => {
    if (isEdit) {
      const currentImagesStr = JSON.stringify(images.map(img => img.preview));
      setHasImageChanges(currentImagesStr !== originalImages);
    }
  }, [images, originalImages, isEdit]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      isMain: images.length === 0, // First image is main by default
      isExisting: false
    }));
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // Ensure first image is main if we have images
      if (newImages.length > 0) {
        return newImages.map((img, i) => ({ ...img, isMain: i === 0 }));
      }
      return newImages;
    });
  };

  const setMainImage = (index) => {
    if (index === 0) return;
    setImages(prev => {
      const newImages = [...prev];
      const temp = newImages[index];
      newImages.splice(index, 1);
      newImages.unshift(temp);
      return newImages.map((img, i) => ({ ...img, isMain: i === 0 }));
    });
  };

  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState('');

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories
  });

  const filteredCategories = categories.filter(c => {
    if (!categorySearchQuery) return true;
    const q = categorySearchQuery.toLowerCase();
    const nameStr = (c.name || '').toLowerCase();
    const enStr = (c.label_en || '').toLowerCase();
    const heStr = (c.label_he || '').toLowerCase();
    const langStr = (c[`label_${language}`] || '').toLowerCase();
    return nameStr.includes(q) || enStr.includes(q) || heStr.includes(q) || langStr.includes(q);
  });

  const createCategoryMutation = useMutation({
    mutationFn: createCategory,
    onSuccess: (newlyCreatedCategory) => {
      toast.success(t('categoryCreated', 'Category created successfully!'));
      queryClient.invalidateQueries(['categories']);
      setNewCategory('');
      setNewCategoryParent('');
      setValue('category', newlyCreatedCategory._id);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create category');
    }
  });

  const handleAddCategory = () => {
    if (newCategory.trim() === '') {
      toast.error(t('categoryNameEmpty', 'Category name cannot be empty'));
      return;
    }
    toast.info(t('autoTranslating', 'Creating category & auto-translating across languages...'));
    createCategoryMutation.mutate({
      name: newCategory.trim().toLowerCase().replace(/\s+/g, '_'),
      label_en: newCategory.trim(),
      icon: newCategoryIcon,
      parent: newCategoryParent || null,
      active: true,
      order: categories.length + 1
    });
  };

  const AVAILABLE_ICONS = ['Tag', 'Briefcase', 'Package', 'Smartphone', 'Home', 'Car', 'Shirt', 'Armchair', 'Music', 'Heart', 'Star', 'MoreHorizontal'];

  const selectedCategoryId = watch('category');
  const selectedCategoryObj = categories.find(c => c._id === selectedCategoryId);
  const categoryAttrKey = getCategoryAttrKey(selectedCategoryObj?.name || selectedCategoryObj?.label_en);

  const saveMutation = useMutation({
    mutationFn: (itemData) => {
        return isEdit ? apiUpdateItem(id, itemData) : apiCreateItem(itemData);
    },
    onSuccess: (newItem) => {
      if (newItem?.requiresApproval) {
        toast.info(t('itemPendingApprovalDesc', 'תודה! המודעה שלך נשלחה לבדיקת מנהל ותפורסם בהקדם. 🛡️'), {
          duration: 6000,
        });
      } else {
        toast.success(isEdit ? t('itemUpdatedSuccess', 'Item updated successfully!') : t('itemListedSuccess', 'Item listed successfully!'));
      }
      queryClient.invalidateQueries(['items', 'my']);
      queryClient.invalidateQueries(['item', id]);
      
      if (!isEdit && newItem && newItem.category) {
        document.cookie = `last_category_search=${newItem.category}; path=/; max-age=${60 * 60 * 24 * 7}`;
      }
      
      if (onSuccess) {
          onSuccess(newItem);
      } else {
          navigate('/my-items');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save item');
    }
  });

  const onSubmit = (data) => {
    if (!user) {
       localStorage.setItem('pendingItemDraft', JSON.stringify(data));
       setPendingSubmitData(data);
       setShowAuthModal(true);
       return;
    }
    submitWithUser(data);
  };

  const submitWithUser = (data) => {
    if (images.length === 0) {
      toast.error(t('mainImageRequiredError', 'Please upload at least one image for your item.'));
      return;
    }

    // Sort images so main is first, then extract either the File or the existing string URL
    const sortedImages = [...images].sort((a, b) => b.isMain - a.isMain).map(img => img.file);
    
    // Collect category specific attributes
    const attributes = {};
    if (categoryAttrKey && CATEGORY_ATTRIBUTES[categoryAttrKey]) {
        CATEGORY_ATTRIBUTES[categoryAttrKey].fields.forEach(field => {
            if (data[field.name] !== undefined) {
                attributes[field.name] = data[field.name];
            }
        });
    }

    const itemData = {
      ...data,
      brand: showBrandInput ? data.brand : '', // Explicitly clear brand if toggle is off
      estimated_value: Number(data.estimated_value),
      images: sortedImages,
      listing_type: 'item',
      price_type: 'fixed',
      attributes: JSON.stringify(attributes), // Send as stringified object for FormData
      import_url: scrapedImportUrl,
      liability_accepted: data.liability_accepted,
    };

    saveMutation.mutate(itemData);
  };

  const onFormError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey) {
      const element = document.querySelector(`[name="${firstErrorKey}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        toast.error(t('pleaseFillRequired', 'Please fill out all required fields'));
      }
    } else if (images.length === 0) {
      toast.error(t('mainImageRequiredError', 'Please upload at least one image for your item.'));
    } else if (errors.liability_accepted) {
      toast.error(t('acceptLiabilityError', 'You must accept the liability terms to post an item.'));
    }
  };

  const hasChanges = isEdit ? (isDirty || hasImageChanges) : true;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const currentImagesStr = JSON.stringify(images.map(img => img.preview));
      if (isDirty || currentImagesStr !== originalImages) {
        if (!isModal) {
            e.preventDefault();
            e.returnValue = '';
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, images, originalImages, isModal]);

  const conditionOptions = ['new', 'like_new', 'excellent', 'good', 'fair'];
  const cashOptions = ['can_add', 'can_receive', 'can_add_or_receive', 'prefer_exchange'];  
  const currencySymbol = currency === 'ILS' ? '₪' : '$';

  if (isEdit && isLoadingItem) {
    return (
        <div className="flex justify-center items-center min-h-[60vh]">
            <Loader2 className="animate-spin w-12 h-12 text-primary" />
        </div>
    );
  }

  return (
    <motion.div
      initial={isModal ? { opacity: 0, scale: 0.95 } : { opacity: 0, y: 20 }}
      animate={isModal ? { opacity: 1, scale: 1 } : { opacity: 1, y: 0 }}
      className={`${isModal ? 'w-full' : 'max-w-3xl mx-auto py-8 px-4'}`}
    >
      <SEO title={isEdit ? t('editItem') : t('listItem')} />
      <div className={`${isModal ? '' : 'bg-card/50 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden'}`}>        <div className={`${isModal ? 'p-0' : 'p-8 md:p-10'}`}>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary">
              {isEdit ? <Save size={32} /> : <Plus size={32} />}
            </div>
            <div>
              <h1 className="text-3xl font-black bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                {isEdit ? t('editItem', 'Edit Item') : t('listYourItem')}
              </h1>
              <p className="text-muted-foreground">{isEdit ? t('editItemSubtitle', 'Update your listing details') : t('createItemSubtitle')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit, onFormError)} className="space-y-8">
            
            {/* Quick Import Section */}
            {!isEdit && (
                 <div className="bg-primary/5 border border-primary/10 rounded-2xl p-6 mb-8">
                     <div className="flex items-center gap-2 mb-4">
                         <Globe className="text-primary" size={20} />
                         <h3 className="font-bold text-sm uppercase tracking-wider">{t('importFromExternal', 'Quick Import from Facebook')}</h3>
                     </div>
                     <p className="text-sm text-muted-foreground mb-4">{t('externalImportDesc', 'Paste a Facebook Marketplace link to auto-fill the details.')}</p>
                     
                     <label className="flex items-start gap-3 p-3 mb-4 rounded-xl cursor-pointer hover:bg-background/50 transition-colors border border-transparent hover:border-white/5">
                        <input 
                            type="checkbox" 
                            checked={acceptedImportTerms}
                            onChange={(e) => setAcceptedImportTerms(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                        />
                        <span className="text-xs text-muted-foreground">
                            {t('importTerms', 'I confirm that I am the rightful owner of this item, that the details provided are accurate, and that I have the legal right to list and trade this item on this platform.')}
                        </span>
                     </label>

                     <AnimatePresence>
                       {acceptedImportTerms && (
                         <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="flex gap-2 overflow-hidden"
                         >
                             <div className="relative flex-1">
                                 <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                 <input 
                                     type="text" 
                                     value={importUrl}
                                     onChange={(e) => setImportUrl(e.target.value)}
                                     placeholder="https://www.facebook.com/marketplace/item/..."
                                     className="w-full bg-background border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 pl-10"
                                 />
                             </div>
                             <button
                                 type="button"
                                 onClick={handleImport}
                                 disabled={isScraping || !importUrl}
                                 className="bg-primary hover:bg-primary/90 text-white px-6 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                             >
                                 {isScraping ? <Loader2 className="w-5 h-5 animate-spin" /> : t('import')}
                             </button>
                         </motion.div>
                       )}
                     </AnimatePresence>
                 </div>
            )}
            
            {/* Basic Info Section */}
            <div className="space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <FileText size={16} /> {t('basicInfo')}
              </h3>
              
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('title')}</label>
                  <div className="relative">
                    <Type className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <input 
                      id="title" 
                      placeholder={t('newOfferTitlePlaceholder')}
                      {...register('title', { required: t('titleRequired') })} 
                      className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 pl-10"
                    />
                  </div>
                  {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('description')}</label>
                  <textarea 
                    id="description" 
                    placeholder={t('newOfferDescriptionPlaceholder')}
                    {...register('description')} 
                    rows="4" 
                    className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl p-4"
                  />
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Tag size={16} /> {t('itemDetails')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('category')}</label>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: t('categoryRequired') }}
                    render={({ field }) => {
                      const selectedCat = categories.find(c => c._id === field.value);
                      return (
                        <div className="space-y-2">
                          {/* Search Input Bar */}
                          <div className="relative">
                            <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <input
                              type="text"
                              placeholder={t('searchCategoryPlaceholder', 'Type to search category or sub-category...')}
                              value={categorySearchQuery}
                              onChange={(e) => setCategorySearchQuery(e.target.value)}
                              className="w-full pl-9 rtl:pl-3 rtl:pr-9 pr-3 py-2.5 bg-secondary/50 border border-input rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                            />
                            {categorySearchQuery && (
                              <button
                                type="button"
                                onClick={() => setCategorySearchQuery('')}
                                className="absolute right-3 rtl:right-auto rtl:left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>

                          {/* Selected Category Pill */}
                          {selectedCat && (
                            <div className="flex items-center justify-between p-2.5 bg-primary/10 border border-primary/30 rounded-xl text-primary font-bold text-sm">
                              <div className="flex items-center gap-2 truncate">
                                <CategoryIcon name={selectedCat.icon} className="w-4 h-4 shrink-0 text-primary" />
                                <span className="truncate">{selectedCat[`label_${language}`] || selectedCat.label_en}</span>
                                {selectedCat.parent && (
                                  <span className="text-xs text-muted-foreground font-normal">
                                    ({categories.find(p => p._id === selectedCat.parent)?.[`label_${language}`] || 'Sub-category'})
                                  </span>
                                )}
                              </div>
                              <span className="text-[10px] uppercase font-black tracking-wider bg-primary text-primary-foreground px-2 py-0.5 rounded-md">Selected</span>
                            </div>
                          )}

                          {/* Category Cards Dropdown List */}
                          <div className="max-h-56 overflow-y-auto bg-card/90 border border-border rounded-xl p-1.5 space-y-1 shadow-inner">
                            {isLoadingCategories ? (
                              <div className="p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                                <Loader2 className="animate-spin w-4 h-4" /> {t('loading', 'Loading...')}
                              </div>
                            ) : filteredCategories.length === 0 ? (
                              <div className="p-4 text-center text-xs text-muted-foreground">
                                {t('noCategoriesFound', 'No categories found matching search.')}
                              </div>
                            ) : (
                              filteredCategories.map(cat => {
                                const isSelected = field.value === cat._id;
                                const isSub = !!cat.parent;
                                const parentCat = isSub ? categories.find(p => p._id === cat.parent) : null;
                                return (
                                  <button
                                    key={cat._id}
                                    type="button"
                                    onClick={() => {
                                      field.onChange(cat._id);
                                      setCategorySearchQuery('');
                                    }}
                                    className={`w-full flex items-center justify-between p-2 rounded-lg text-xs transition-all text-left rtl:text-right ${
                                      isSelected
                                        ? 'bg-primary text-primary-foreground font-black shadow-xs'
                                        : isSub
                                        ? 'hover:bg-secondary/60 text-foreground font-medium pl-6 rtl:pl-2 rtl:pr-6 border-l-2 border-primary/30'
                                        : 'hover:bg-secondary/80 text-foreground font-bold bg-secondary/30'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 truncate">
                                      <CategoryIcon name={cat.icon} className={`w-4 h-4 shrink-0 ${isSelected ? 'text-primary-foreground' : 'text-primary'}`} />
                                      <span className="truncate">{cat[`label_${language}`] || cat.label_en}</span>
                                    </div>
                                    {isSub && parentCat && (
                                      <span className={`text-[10px] opacity-75 shrink-0 ${isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                                        {parentCat[`label_${language}`] || parentCat.label_en}
                                      </span>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />
                  {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category.message}</p>}
                  
                  {/* Inline Add Category - Admin Only */}
                  {user?.role === 'admin' && (
                    <div className="mt-4 flex flex-col gap-3 border p-3.5 rounded-2xl bg-card/60 shadow-xs">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-wider text-primary">{t('createNewCategory', 'Create Category (Admin)')}</p>
                        <span className="text-[9px] bg-primary/20 text-primary px-2 py-0.5 rounded-full font-black">ADMIN ONLY</span>
                      </div>

                      <div className="flex flex-col gap-2">
                        {/* Parent Category Selector (for creating sub-categories) */}
                        <select
                          value={newCategoryParent}
                          onChange={(e) => setNewCategoryParent(e.target.value)}
                          className="w-full bg-secondary/50 border-transparent rounded-lg py-1.5 px-3 text-xs focus:ring-primary"
                        >
                          <option value="">Top-Level Main Category</option>
                          {categories.filter(c => !c.parent).map(c => (
                            <option key={c._id} value={c._id}>Sub-category under: {c[`label_${language}`] || c.label_en}</option>
                          ))}
                        </select>

                        {/* Icon & Name Input */}
                        <div className="flex gap-2 items-center">
                          <div className="relative" ref={iconDropdownRef}>
                            <button 
                              type="button"
                              onClick={() => setShowIconDropdown(!showIconDropdown)}
                              className="bg-secondary/50 hover:bg-secondary/70 border-transparent rounded-lg py-1.5 px-3 text-sm focus:ring-primary h-[34px] flex items-center justify-center transition-colors"
                              title={t('selectIcon', 'Select Icon')}
                            >
                              {(() => {
                                const IconCmp = LucideIcons[newCategoryIcon];
                                return IconCmp ? <IconCmp size={16} /> : <Tag size={16} />;
                              })()}
                            </button>
                            {showIconDropdown && (
                              <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-white/10 rounded-xl shadow-xl z-50 p-2 grid grid-cols-4 gap-1 max-h-48 overflow-y-auto">
                                {AVAILABLE_ICONS.map(icon => {
                                  const IconCmp = LucideIcons[icon];
                                  if (!IconCmp) return null;
                                  return (
                                    <button
                                      key={icon}
                                      type="button"
                                      onClick={() => {
                                        setNewCategoryIcon(icon);
                                        setShowIconDropdown(false);
                                      }}
                                      className={`p-2 rounded-lg flex items-center justify-center transition-colors ${newCategoryIcon === icon ? 'bg-primary text-primary-content' : 'hover:bg-secondary/50'}`}
                                      title={icon}
                                    >
                                      <IconCmp size={16} />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <input
                            type="text"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            className="flex-1 bg-secondary/50 border-transparent rounded-lg py-1.5 px-3 text-xs focus:ring-primary"
                            placeholder={t('newCategoryName', 'Category name...')}
                          />
                          <button
                            type="button"
                            onClick={handleAddCategory}
                            disabled={createCategoryMutation.isLoading}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all"
                          >
                            {createCategoryMutation.isLoading ? <Loader2 className="animate-spin h-3.5 w-3.5" /> : t('addCategory')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('estimatedValue')} ({currencySymbol})
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <input 
                      type="number" 
                      placeholder="0"
                      {...register('estimated_value', { required: t('valueRequired'), min: { value: 1, message: t('minimumValue') } })} 
                      className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 pl-10"
                    />
                  </div>
                  {errors.estimated_value && <p className="text-red-500 text-xs mt-1">{errors.estimated_value.message}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2">{t('condition')}</label>
                    <Controller
                      name="condition"
                      control={control}
                      rules={{ required: t('conditionRequired') }}
                      render={({ field }) => (
                        <select {...field} className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4">
                            <option value="">{t('selectCondition')}</option>
                            {conditionOptions.map(opt => <option key={opt} value={opt}>{t(opt)}</option>)}
                        </select>
                      )}
                    />
                    {errors.condition && <p className="text-red-500 text-xs mt-1">{errors.condition.message}</p>}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-2xl border border-white/5">
                    <div>
                      <p className="font-bold text-sm">{t('hasBrand', 'Specify Brand?')}</p>
                      <p className="text-xs text-muted-foreground">{t('brandToggleDesc', 'Toggle to add a brand name, otherwise it shows as unbranded.')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={showBrandInput}
                        onChange={() => setShowBrandInput(!showBrandInput)}
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {showBrandInput && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-2"
                    >
                      <label className="block text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">{t('brandLabel', 'Brand')}</label>
                      <input 
                        placeholder={t('brandPlaceholder', 'e.g. Nike, Apple, Toyota')}
                        maxLength={25}
                        {...register('brand', {
                          maxLength: {
                            value: 25,
                            message: t('brandMaxChars', 'Brand name cannot exceed 25 characters')
                          }
                        })} 
                        className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4 transition-all"
                      />
                    </motion.div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('location')}</label>
                  <div className="flex gap-2">
                    <input 
                        placeholder={t('locationPlaceholder')}
                        {...register('location', {
                          onBlur: async (e) => {
                            const val = e.target.value;
                            if (val && val.length > 2) {
                                try {
                                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&limit=1`);
                                    const data = await res.json();
                                    if (data && data[0]) {
                                        setValue('longitude', parseFloat(data[0].lon));
                                        setValue('latitude', parseFloat(data[0].lat));
                                        // Silent success to avoid annoying user
                                    }
                                } catch (err) {
                                    console.error('Geocoding error:', err);
                                }
                            }
                          }
                        })} 
                        className="flex-1 bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            if ("geolocation" in navigator) {
                                navigator.geolocation.getCurrentPosition((position) => {
                                    setValue('longitude', position.coords.longitude);
                                    setValue('latitude', position.coords.latitude);
                                    toast.success(t('locationCaptured', 'Location coordinates captured!'));
                                });
                            }
                        }}
                        className="p-3 bg-secondary/50 hover:bg-primary/20 hover:text-primary rounded-xl transition-all"
                        title={t('useCurrentLocation', 'Use Current Location')}
                    >
                        <Navigation size={20} />
                    </button>
                  </div>
                  <input type="hidden" {...register('longitude')} />
                  <input type="hidden" {...register('latitude')} />
                  </div>
                  </div>
                  </div>

                  {/* Dynamic Category Attributes */}
                  {categoryAttrKey && CATEGORY_ATTRIBUTES[categoryAttrKey] && (
                  <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-6"
                  >
                  <h3 className="text-sm font-bold uppercase tracking-wider text-primary flex items-center gap-2">
                  <Plus size={16} /> {t('additionalDetails', 'Additional Details')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-primary/5 rounded-[2rem] border border-primary/10">
                  {CATEGORY_ATTRIBUTES[categoryAttrKey].fields.map(field => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium mb-2">{language === 'he' ? field.label_he : field.label_en}</label>
                      {field.type === 'select' ? (
                        <select 
                          {...register(field.name)} 
                          className="w-full bg-background border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4"
                        >
                          <option value="">{t('select', 'Select')}...</option>
                          {field.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : field.type === 'boolean' ? (
                        <div className="flex items-center h-[52px]">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" {...register(field.name)} className="sr-only peer" />
                            <div className="w-11 h-6 bg-muted rounded-full peer peer-focus:ring-4 peer-focus:ring-primary/20 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                          </label>
                        </div>
                      ) : (
                        <input 
                          type={field.type} 
                          {...register(field.name)} 
                          min={field.min}
                          max={field.max}
                          placeholder={`${language === 'he' ? 'הכנס' : 'Enter'} ${language === 'he' ? field.label_he : field.label_en}`}
                          className="w-full bg-background border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4"
                        />
                      )}
                    </div>
                  ))}
                  </div>
                  </motion.div>
                  )}

            {/* Media Section */}
            <div className="space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <ImageIcon size={16} /> {t('media')}
              </h3>

              <div className="space-y-4">
                <div className="relative group cursor-pointer">
                  <div className="border-2 border-dashed border-muted hover:border-primary/50 hover:bg-muted/50 rounded-2xl p-10 transition-all flex flex-col items-center justify-center text-center">
                    <UploadCloud className="h-12 w-12 text-muted-foreground mb-3 group-hover:scale-110 transition-transform" />
                    <p className="text-sm font-black text-primary uppercase tracking-widest">{t('uploadFiles')}</p>
                    <p className="text-xs text-muted-foreground mt-1 uppercase font-bold">{t('orDragAndDrop')}</p>
                    <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" multiple onChange={handleImageChange} accept="image/*" />
                  </div>
                </div>

                {images.length > 0 && (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={images.map(img => img.preview)} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {images.map((img, i) => (
                          <SortableImage 
                              key={img.preview} 
                              img={img} 
                              i={i} 
                              total={images.length}
                              removeImage={removeImage} 
                              setMainImage={setMainImage} 
                              moveImage={moveImage}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>

            {/* Trading Preferences */}
            <div className="space-y-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Info size={16} /> {t('tradingPreferences')}
                </h3>

                <div className="grid gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <label className="block text-sm font-medium">{t('lookingForOptional')}</label>
                            <span className="text-xs text-muted-foreground">({t('lookingForHelp')})</span>
                        </div>
                        <Controller
                        name="looking_for"
                        control={control}
                        defaultValue={[]}
                        render={({ field }) => {
                            const selectedIds = field.value || [];
                            const handleAdd = (e) => {
                                const id = e.target.value;
                                if (id && !selectedIds.includes(id)) {
                                    field.onChange([...selectedIds, id]);
                                }
                                e.target.value = "";
                            };
                            const handleRemove = (idToRemove) => {
                                field.onChange(selectedIds.filter(id => id !== idToRemove));
                            };

                            return (
                                <div className="space-y-3">
                                    <select 
                                        onChange={handleAdd} 
                                        className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4"
                                        defaultValue=""
                                    >
                                        <option value="" disabled>{t('selectLookingFor')}</option>
                                        {isLoadingCategories ? <option>{t('loading')}</option> : 
                                            categories
                                                .filter(c => !selectedIds.includes(c._id))
                                                .filter(c => c.name.toLowerCase() !== 'services' && c.name.toLowerCase() !== 'service' && c.label_en?.toLowerCase() !== 'services')
                                                .map(c => <option key={c._id} value={c._id}>{c[`label_${language}`] || c.label_en}</option>)
                                        }
                                    </select>

                                    {selectedIds.length > 0 && (
                                        <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-xl border border-dashed border-white/10">
                                            {selectedIds.map(id => {
                                                const cat = categories.find(c => c._id === id);
                                                const label = cat ? (cat[`label_${language}`] || cat.label_en) : id;
                                                return (
                                                    <div key={id} className="flex items-center gap-1 bg-primary text-primary-content px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                                                        <span>{label}</span>
                                                        <button type="button" onClick={() => handleRemove(id)} className="hover:text-red-200 transition-colors"><X size={14} /></button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">{t('cashFlexibility')}</label>
                        <Controller
                        name="cash_flexibility"
                        control={control}
                        defaultValue="prefer_exchange"
                        render={({ field }) => (
                            <select {...field} className="w-full bg-secondary/50 border-transparent focus:border-primary focus:ring-primary rounded-xl py-3 px-4">
                                {cashOptions.map(opt => <option key={opt} value={opt}>{t(opt)}</option>)}
                            </select>
                        )}
                        />
                    </div>

                    <div className="bg-secondary/30 p-4 rounded-2xl border border-dashed border-white/10">
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input 
                                type="checkbox" 
                                {...register('open_to_other_offers')}
                                className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                            />
                            <div className="flex-1">
                                <span className="block text-sm font-bold group-hover:text-primary transition-colors">{t('openToOtherOffers')}</span>
                                <span className="block text-xs text-muted-foreground">{t('openToOtherOffersDesc')}</span>
                            </div>
                            </label>

                            {/* Item Visibility & Availability */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                            <label className="group flex items-start gap-4 p-4 bg-secondary/20 rounded-2xl border border-white/5 hover:border-primary/20 hover:bg-secondary/30 transition-all cursor-pointer">
                               <input 
                                   type="checkbox" 
                                   {...register('is_visible')}
                                   className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                               />
                               <div className="flex-1">
                                   <span className="block text-sm font-bold group-hover:text-primary transition-colors">{t('isVisible', 'Show on Profile')}</span>
                                   <span className="block text-xs text-muted-foreground">{t('isVisibleDesc', 'Decide if this item is visible on your public profile.')}</span>
                               </div>
                            </label>

                            <label className="group flex items-start gap-4 p-4 bg-secondary/20 rounded-2xl border border-white/5 hover:border-primary/20 hover:bg-secondary/30 transition-all cursor-pointer">
                               <input 
                                   type="checkbox" 
                                   {...register('is_available')}
                                   className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                               />
                               <div className="flex-1">
                                   <span className="block text-sm font-bold group-hover:text-primary transition-colors">{t('isAvailable', 'Available for Trade')}</span>
                                   <span className="block text-xs text-muted-foreground">{t('isAvailableDesc', 'Mark this item as currently available for swap offers.')}</span>
                               </div>
                            </label>
                            </div>
                            </div>                </div>
            </div>
            
            <div className="pt-4 border-t border-white/10 mt-8">
               <label className="flex items-start gap-4 p-4 rounded-xl cursor-pointer">
                   <input 
                       type="checkbox" 
                       {...register('liability_accepted', { required: true })}
                       className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                   />
                   <div className="flex-1">
                       <span className="block text-sm text-muted-foreground">
                           {t('liabilityTerms', 'I confirm that I am the legal owner of this item and accept full responsibility for this listing. If imported from Facebook, I confirm this is my own listing.')}
                       </span>
                   </div>
               </label>
            </div>
            
            <div className="pt-4">
              <button 
                type="submit" 
                disabled={saveMutation.isLoading || (isEdit && !hasChanges)}
                className={`w-full flex justify-center items-center gap-2 py-4 px-6 border border-transparent rounded-xl shadow-lg text-lg font-bold text-white transition-all active:scale-[0.98] ${
                  (saveMutation.isLoading || (isEdit && !hasChanges))
                    ? 'bg-gray-500 cursor-not-allowed opacity-50 shadow-none'
                    : 'bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-700 shadow-primary/25'
                }`}
              >
                {saveMutation.isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isEdit ? t('saving', 'Saving...') : t('listing', 'Listing...')}
                  </>
                ) : (
                  isEdit ? t('saveChanges', 'Save Changes') : t('listMyItem')
                )}
              </button>            </div>
          </form>
        </div>
      </div>
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onLoginSuccess={handleLoginSuccess}
      />
    </motion.div>
  );
}
